import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { getFullOrganizationData } from '@app/actions/organization';
import { LineOfBusiness } from '@app/components/Admin/Operational/Brands/partials/types';
import type { MediaRestrictions } from '@app/components/Admin/Organization/MediaRestrictions/types';
import { AutoRepliesConfig, AutoReply } from '@app/components/Admin/types';
import { UPDATE_ORGANIZATION_SETTING } from '@app/constants/actions';
import { AUTO_REPLY_TYPES } from '@app/constants/user-mgmt';
import {
  formatAutoReplyPayload,
  formatAutoReplyResponse,
} from '@app/helpers/auto-replies';
import { AutoCloseConfig } from '@app/types/auto-close-config';
import { toaster } from '@chakra-primitives/toaster';

import {
  UPDATE_ORG_SETTING,
  UPDATE_LINE_OF_BUSINESS,
  GET_FULL_ORG_DATA,
  GET_ORGANIZATION_FEATURES,
  GET_ORGANIZATION_FEATURE_AUDIT_LOG,
  UPDATE_ORGANIZATION_FEATURE,
  UPDATE_ORGANIZATION_BRANDING as ENDPOINT_UPDATE_ORGANIZATION_BRANDING,
  UPDATE_AUTO_RESEND_WELCOME_SETTINGS,
  UPDATE_QUIET_HOURS_SETTINGS,
  GET_RULESET,
  GET_ORGANIZATION_ENTITLEMENTS,
  UPDATE_ORGANIZATION_ENTITLEMENTS,
  GET_AUTO_CLOSE_CONFIGS,
  CREATE_AUTO_CLOSE_CONFIG,
  UPDATE_AUTO_CLOSE_CONFIG,
  GET_COMPOSITE_AUTO_CLOSE_CONFIGS,
} from '../constants/endpoints';
import { ADMIN_QUERY_KEYS, QUERY_KEYS } from '../constants/service';
import {
  putData,
  patchData,
  fetchData,
  postData,
} from '../hooks/react-query-helpers';
import { useOrganizationId } from '../selectors/organization';
import type { QuietHoursSettings } from '../types/api/organization';

type ThunkAppDispatch = ThunkDispatch<unknown, void, Action>;

// Endpoints
const GET_ORG_AUTOREPLIES = (orgName: string) =>
  `/api/organization/${orgName}/autoreplies`;
const UPDATE_ORG_AUTOREPLY = (orgName: string, autoReplyId: string) =>
  `/api/organization/${orgName}/autoreplies/${autoReplyId}`;
const CREATE_ORG_AUTOREPLY = (orgName: string) =>
  `/api/organization/${orgName}/autoreplies`;

// Types
export type AutoResendWelcomeAllowedTimes = {
  monday?: number[];
  tuesday?: number[];
  wednesday?: number[];
  thursday?: number[];
  friday?: number[];
  saturday?: number[];
  sunday?: number[];
};

export enum InboxNotificationBehavior {
  UNREAD = 'unread',
  UNADDRESSED = 'unaddressed',
}

export const INBOX_NOTIFICATION_BEHAVIOR_ITEMS: Array<{
  value: InboxNotificationBehavior;
  label: string;
}> = [
  { value: InboxNotificationBehavior.UNREAD, label: 'Unread' },
  { value: InboxNotificationBehavior.UNADDRESSED, label: 'Unaddressed' },
];

export type AutoResendWelcomeConfig = {
  enabled: boolean;
  timeout: number;
  maxAttempts: number;
  allowedTimes: AutoResendWelcomeAllowedTimes;
  timeZone?: string;
};

export type OrganizationSettings = {
  autoMessagePrefix?: {
    prefix: string;
  };
  userMessagePrefix?: {
    isTitleEnabled: boolean;
    isCaseIdEnabled: boolean;
    isPhoneNumberEnabled: boolean;
  };
  checkSecondaryOOOStatus?: boolean;
  caseTypes?: Record<string, unknown>;
  autoResendWelcome?: AutoResendWelcomeConfig;
  quietHours?: QuietHoursSettings;
  inboxBehavior?: {
    behavior: InboxNotificationBehavior;
    lockedByAdmin: boolean;
  };
  internalUnreadMessageSetting?: {
    enabled: boolean;
    lockedByAdmin: boolean;
  };
  snoozeUnaddressedEnabled?: boolean;
  dismissUnaddressedEnabled?: boolean;
  ignoreOptInYesUnaddressedEnabled?: boolean;
  fabricAgents?: {
    enabled?: boolean;
    orgCreatedAt?: string;
    agentMessagePrefix?: {
      enabled?: boolean;
      value?: string;
    };
  };
  languagePreference?: string;
  mediaRestrictions?: MediaRestrictions | null;
};

export type UpdateOrgSettingData = {
  organizationId: string;
  organizationSettings: OrganizationSettings;
};

export type UpdateOrgSsoData = {
  organizationId: string;
  identityProvider: string;
  jit: boolean;
  domainName: string[];
};

export type UpdateLoBsData = {
  organizationId: string;
  linesOfBusiness: Array<{
    displayName: string;
    type: string;
    subType: string;
    branding: string;
    archived: boolean;
  }>;
};

type UpdateLineOfBusinessData = {
  organizationId: string;
  lineOfBusinessId: string;
  [key: string]: unknown;
};

type UpdateBrandingData = {
  organizationId: string;
  [key: string]: unknown;
};

type LineOfBusinessResponse = {
  archived?: boolean;
  type?: string;
  subType?: string;
  branding?: string;
};

export type OrgAutoReply = {
  pk?: string;
  id?: string;
  active?: boolean;
  type?: string;
  title?: string;
  sendCriteria?: {
    timeZone?: string;
    minutesInactiveThreshold?: number;
    timing?: string;
    days?: Record<string, unknown>;
  };
  message?: {
    during?: {
      value?: string;
    };
  };
};

type UpdateOrgAutoReplyData = {
  organizationName: string;
  autoReplyId: string;
  autoReply: OrgAutoReply;
};

type UpdateAutoResendWelcomeSettingsData = {
  autoResendWelcome: AutoResendWelcomeConfig;
};

type UpdateQuietHoursSettingsData = {
  quietHours: QuietHoursSettings;
};

export type BaseCondition = {
  fact: string;
  operator?: string;
  value?: string | boolean;
};

export type BaseRule = {
  conditions: {
    all: BaseCondition[];
  };
  event?: {
    type: string;
    params?: {
      message?: string;
      delayInMs?: number;
      id?: string;
    };
  };
};

export type MessageTemplate = {
  id?: string;
  body?: string;
  tokens?: Array<{ token: string }>;
  displayName?: string;
};

export type MessageRule = {
  id?: string;
  enabled?: boolean;
  rule: BaseRule;
  messageTemplate?: MessageTemplate;
};

export type MessageRuleset = {
  id?: string;
  rules?: MessageRule[];
  lineOfBusinessId?: string;
  defaultVersion?: string;
};

export type WelcomeMessageRule = MessageRule;
export type WelcomeMessageRuleset = MessageRuleset;

export type ReassignmentMessageRule = MessageRule;
export type ReassignmentMessageRuleset = Omit<
  MessageRuleset,
  'lineOfBusinessId'
>;

export type AssignmentMessageRule = MessageRule;
export type AssignmentMessageRuleset = Omit<MessageRuleset, 'lineOfBusinessId'>;
export type MptMessageRule = MessageRule;
export type MptMessageRuleset = Omit<MessageRuleset, 'lineOfBusinessId'>;

export type FullOrganizationData = {
  _id: string;
  name: string;
  jit?: boolean;
  domainName?: string[];
  organizationSettings?: OrganizationSettings;
  [key: string]: unknown;
  linesOfBusiness?: LineOfBusiness[];
  branding?: string[];
};

const getGeneralAutoReply = (autoReplies: OrgAutoReply[], type?: string) => {
  const repliesArray = Array.isArray(autoReplies) ? autoReplies : [autoReplies];
  if (type) {
    return repliesArray.find((reply) => reply.type === type);
  }
  return repliesArray.find((reply) => reply.type === AUTO_REPLY_TYPES.GENERAL);
};

// Queries
export const useFullOrganizationData = (
  orgId: string,
  options?: { enabled?: boolean },
): UseQueryResult<FullOrganizationData, Error> =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.fullOrgData(orgId),
    queryFn: () =>
      fetchData(GET_FULL_ORG_DATA(orgId)) as Promise<FullOrganizationData>,
    enabled: !!orgId && (options?.enabled ?? true),
  });

export const useOrgAutoReply = (orgName: string, type?: string) =>
  useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.orgAutoReply(orgName), type],
    queryFn: async () => {
      const replies = (await fetchData(
        GET_ORG_AUTOREPLIES(orgName.toUpperCase()),
      )) as OrgAutoReply[];
      const v1Replies = formatAutoReplyResponse(
        replies as unknown as AutoRepliesConfig,
        `ORG#${orgName.toUpperCase()}`,
      );
      return getGeneralAutoReply(v1Replies as unknown as OrgAutoReply[], type);
    },
    enabled: !!orgName,
  });

export const useWelcomeMessageRuleset = (
  rulesetType: string,
  partnerQuery = '',
) =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.welcomeMessageRuleset(rulesetType, partnerQuery),
    queryFn: () =>
      fetchData(
        GET_RULESET(rulesetType, partnerQuery),
      ) as Promise<WelcomeMessageRuleset>,
    enabled: !!rulesetType,
  });

export const useReassignmentMessageRuleset = (
  rulesetType: string,
  partnerQuery = '',
) =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.reassignmentMessageRuleset(
      rulesetType,
      partnerQuery,
    ),
    queryFn: () =>
      fetchData(
        GET_RULESET(rulesetType, partnerQuery),
      ) as Promise<ReassignmentMessageRuleset>,
    enabled: !!rulesetType,
  });

export const useAssignmentMessageRuleset = (
  rulesetType: string,
  partnerQuery = '',
) =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.assignmentMessageRuleset(
      rulesetType,
      partnerQuery,
    ),
    queryFn: () =>
      fetchData(
        GET_RULESET(rulesetType, partnerQuery),
      ) as Promise<AssignmentMessageRuleset>,
    enabled: !!rulesetType,
  });

export const useMptMessageRuleset = (partnerQuery = '') =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.mptMessageRuleset(partnerQuery),
    queryFn: () =>
      fetchData(
        GET_RULESET('MULTIPARTY_RULESET', partnerQuery),
      ) as Promise<MptMessageRuleset>,
  });

export const useLockedOutMessageRuleset = (partnerQuery = '') =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.lockedOutMessageRuleset(partnerQuery),
    queryFn: () =>
      fetchData(
        GET_RULESET('LOCKED_OUT_MESSAGE_RULESET', partnerQuery),
      ) as Promise<ReassignmentMessageRuleset>,
  });

export const useInvalidOptInMessageRuleset = (partnerQuery = '') =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.invalidOptInMessageRuleset(partnerQuery),
    queryFn: () =>
      fetchData(
        GET_RULESET('WELCOME_FLOW_OPT_IN_ERROR', partnerQuery),
      ) as Promise<ReassignmentMessageRuleset>,
  });

export type ProductPhase =
  | 'GENERAL_AVAILABILITY'
  | 'LIMITED_AVAILABILITY'
  | 'OPEN_BETA'
  | 'CLOSED_BETA'
  | 'ALPHA'
  | 'PRE_ALPHA';

export type OrganizationFeature = {
  id: string;
  featureName: string;
  displayName: string;
  description: string;
  category: string;
  hasAccess: boolean;
  enabled: boolean;
  defaultEnabled: boolean;
  dependencies: OrganizationFeature[];
  hasConfiguration?: boolean;
  productPhase: ProductPhase;
};

export type FeatureScope = 'public' | 'internal';

export const useOrganizationFeatures = (scope: FeatureScope = 'public') =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.organizationFeatures(scope),
    queryFn: () =>
      fetchData(GET_ORGANIZATION_FEATURES(scope)) as Promise<
        OrganizationFeature[]
      >,
  });

export type UpdateOrganizationFeatureData = {
  featureName: string;
  enabled: boolean;
};

export type OrganizationFeatureAuditActor = {
  id: string | null;
  displayName: string | null;
  email: string | null;
  fallbackLabel: string;
};

export type OrganizationFeatureAuditEvent = {
  id: string;
  timestamp: string;
  featureName: string | null;
  featureDisplayName: string;
  eventType: string;
  eventLabel: string;
  actor: OrganizationFeatureAuditActor;
};

export type OrganizationFeatureAuditLogParams = {
  startDate?: string;
  endDate?: string;
  featureName?: string;
  limit?: number;
  offset?: number;
};

export type OrganizationFeatureAuditLogResponse = {
  events: OrganizationFeatureAuditEvent[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

const buildOrganizationFeatureAuditLogQueryString = (
  params: OrganizationFeatureAuditLogParams,
) => {
  const searchParams = new URLSearchParams();

  if (params.startDate) {
    searchParams.append('startDate', params.startDate);
  }

  if (params.endDate) {
    searchParams.append('endDate', params.endDate);
  }

  if (params.featureName) {
    searchParams.append('featureName', params.featureName);
  }

  if (params.limit !== undefined) {
    searchParams.append('limit', params.limit.toString());
  }

  if (params.offset !== undefined) {
    searchParams.append('offset', params.offset.toString());
  }

  return searchParams.toString();
};

export const fetchOrganizationFeatureAuditLog = (
  params: OrganizationFeatureAuditLogParams = {},
) =>
  fetchData(
    GET_ORGANIZATION_FEATURE_AUDIT_LOG(
      buildOrganizationFeatureAuditLogQueryString(params),
    ),
    { handleError: true },
  ) as Promise<OrganizationFeatureAuditLogResponse>;

export const useUpdateOrganizationFeature = () => {
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();
  const dispatch: ThunkAppDispatch = useDispatch();

  return useMutation({
    mutationFn: async (data: UpdateOrganizationFeatureData) =>
      patchData(
        UPDATE_ORGANIZATION_FEATURE(),
        {
          featureName: data.featureName,
          enabled: data.enabled,
        },
        { handleError: true },
      ),
    onSuccess: () => {
      // Invalidate organization features query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION, 'features'],
      });
      // Also invalidate full org data in case it's used elsewhere
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: ADMIN_QUERY_KEYS.fullOrgData(organizationId),
        });
        // And invalidate org data in Redux
        dispatch(getFullOrganizationData(organizationId));
      }
      toaster.create({
        title: 'Feature updated successfully',
        type: 'success',
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update feature',
        description: error.message,
        type: 'error',
      });
    },
  });
};

export type OrganizationEntitlement = {
  id: string;
  entitlementName: string;
  featureCount: number;
  enabled: boolean;
};

export type EntitlementsMetadata = {
  lastUpdatedBy: string | null;
  lastUpdatedDate: string | null;
};

export type OrganizationEntitlementsResponse = {
  entitlements: OrganizationEntitlement[];
  metadata: EntitlementsMetadata;
};

export const useOrganizationEntitlements = () =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.organizationEntitlements(),
    queryFn: () =>
      fetchData(
        GET_ORGANIZATION_ENTITLEMENTS(),
      ) as Promise<OrganizationEntitlementsResponse>,
  });

export const useUpdateOrganizationEntitlements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { entitlementName: string; enabled: boolean }) =>
      patchData(
        UPDATE_ORGANIZATION_ENTITLEMENTS(),
        {
          entitlementName: data.entitlementName,
          enabled: data.enabled,
        },
        { handleError: true },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.organizationEntitlements(),
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION, 'features'],
      });
      toaster.create({
        title: 'Entitlement updated successfully',
        type: 'success',
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update entitlement',
        description: error.message,
        type: 'error',
      });
    },
  });
};

// Mutations
export const useUpdateOrgSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateOrgSettingData) =>
      putData(UPDATE_ORG_SETTING(data.organizationId), data),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.orgSettings(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.fullOrgData(variables.organizationId),
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update organization settings',
        description: error.message,
        type: 'error',
      });
    },
  });
};

export const useUpdateOrgSso = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateOrgSsoData) =>
      putData(UPDATE_ORG_SETTING(data.organizationId), data),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.orgSettings(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.fullOrgData(variables.organizationId),
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update organization settings',
        description: error.message,
        type: 'error',
      });
    },
  });
};

export const useUpdateLoBs = (skipToast = false) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateLoBsData) =>
      putData(UPDATE_ORG_SETTING(data.organizationId), data),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.orgSettings(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.fullOrgData(variables.organizationId),
      });

      if (!skipToast) {
        toaster.create({
          title: 'Lines of business saved',
          type: 'success',
        });
      }
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to save lines of business',
        description: error.message,
        type: 'error',
      });
    },
  });
};

export const useUpdateLineOfBusiness = (skipToast = false) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateLineOfBusinessData) =>
      putData(
        UPDATE_LINE_OF_BUSINESS(data.organizationId, data.lineOfBusinessId),
        data,
      ) as Promise<LineOfBusinessResponse>,
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.lineOfBusiness(
          variables.organizationId,
          variables.lineOfBusinessId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.fullOrgData(variables.organizationId),
      });

      if (!skipToast) {
        toaster.create({
          title: 'Line of business updated successfully',
          type: 'success',
        });
      }
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update line of business',
        description: error.message,
        type: 'error',
      });
    },
  });
};

export const useUpdateBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateBrandingData) =>
      putData(ENDPOINT_UPDATE_ORGANIZATION_BRANDING(data.organizationId), data),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.branding(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.fullOrgData(variables.organizationId),
      });

      toaster.create({
        title: 'Branding updated successfully',
        type: 'success',
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update branding',
        description: error.message,
        type: 'error',
      });
    },
  });
};

export const useUpdateOrgAutoReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateOrgAutoReplyData) =>
      putData(
        UPDATE_ORG_AUTOREPLY(data.organizationName, data.autoReplyId),
        formatAutoReplyPayload(data?.autoReply as unknown as AutoReply),
      ) as Promise<OrgAutoReply>,
    onSuccess: (_responseData, variables) => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.orgAutoReply(variables.organizationName),
      });

      toaster.create({
        title: 'Inactive Auto Reply Updated',
        description:
          'The inactive auto reply settings have been successfully saved.',
        type: 'success',
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Save Failed',
        description:
          error.message ||
          'Failed to save the inactive auto reply settings. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useCreateOrgAutoReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      organizationName: string;
      autoReply: OrgAutoReply;
    }) =>
      postData(
        CREATE_ORG_AUTOREPLY(data.organizationName),
        formatAutoReplyPayload(data.autoReply as unknown as AutoReply),
      ) as Promise<OrgAutoReply>,
    onSuccess: (_responseData, variables) => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.orgAutoReply(variables.organizationName),
      });
    },
  });
};

export const useUpdateAutoResendWelcomeSettings = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    FullOrganizationData,
    Error,
    UpdateAutoResendWelcomeSettingsData
  >({
    mutationFn: async (data) =>
      putData(UPDATE_AUTO_RESEND_WELCOME_SETTINGS(), data),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ADMIN_QUERY_KEYS.fullOrgData(organizationId),
        data,
      );

      toaster.create({
        title: 'Auto Resend Welcome Settings Updated',
        description:
          'The auto resend welcome settings have been successfully saved.',
        type: 'success',
      });
    },
    onError: (error) => {
      toaster.create({
        title: 'Save Failed',
        description:
          error.message ||
          'Failed to save the auto resend welcome settings. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateQuietHoursSettings = (organizationId: string) => {
  const queryClient = useQueryClient();
  const dispatch: ThunkAppDispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation<FullOrganizationData, Error, UpdateQuietHoursSettingsData>(
    {
      mutationFn: async (data) => putData(UPDATE_QUIET_HOURS_SETTINGS(), data),
      onSuccess: (data) => {
        queryClient.setQueryData(
          ADMIN_QUERY_KEYS.fullOrgData(organizationId),
          data,
        );
        queryClient.setQueryData(
          [QUERY_KEYS.ORGANIZATION, organizationId],
          data,
        );
        dispatch({ type: UPDATE_ORGANIZATION_SETTING, payload: data });

        toaster.create({
          title: t('quietHoursSaveSuccessTitle'),
          description: t('quietHoursSaveSuccessDescription'),
          type: 'success',
        });
      },
      onError: (error) => {
        toaster.create({
          title: t('quietHoursSaveErrorTitle'),
          description: error.message || t('quietHoursSaveErrorDescription'),
          type: 'error',
        });
      },
    },
  );
};

export const useAutoCloseConfigs = (partnerQuery = '') =>
  useQuery({
    queryKey: ADMIN_QUERY_KEYS.autoCloseConfigs(partnerQuery),
    queryFn: () =>
      fetchData(
        GET_AUTO_CLOSE_CONFIGS(partnerQuery),
      ) as Promise<AutoCloseConfig>,
  });

export const useCompositeAutoCloseConfigs = (partnerQuery = '') =>
  useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.autoCloseConfigs(partnerQuery), 'composite'],
    queryFn: () =>
      fetchData(
        GET_COMPOSITE_AUTO_CLOSE_CONFIGS(partnerQuery),
      ) as Promise<AutoCloseConfig>,
  });

export const useCreateAutoCloseConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AutoCloseConfig) =>
      postData(CREATE_AUTO_CLOSE_CONFIG, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.autoCloseConfigs(),
      });
      toaster.create({
        title: 'Auto close configuration saved successfully',
        type: 'success',
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to save auto close configuration',
        description: error.message,
        type: 'error',
      });
    },
  });
};

export const useUpdateAutoCloseConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      configId,
      data,
    }: {
      configId: string;
      data: AutoCloseConfig;
    }) => putData(UPDATE_AUTO_CLOSE_CONFIG(configId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_QUERY_KEYS.autoCloseConfigs(),
      });
      toaster.create({
        title: 'Auto close configuration saved successfully',
        type: 'success',
      });
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to save auto close configuration',
        description: error.message,
        type: 'error',
      });
    },
  });
};
